import { Link } from "react-router-dom";
import { SITE, has } from "@/config/site";
import Logo from "@/components/Logo";

export default function Footer() {
  const lending = SITE.LENDING_ENABLED;

  const instalmentsCol = lending
    ? [
        { href: "/#how-instalments-work", label: "How instalments work" },
        { href: "/parent/academic-advance", label: "Check what you can borrow" },
        { href: "/pricing", label: "Pricing & costs" },
        { href: "/#for-schools", label: "For schools" },
        { href: "/#for-agents", label: "For agents" },
      ]
    : null;

  const companyCol = [
    { href: "/about", label: "About us" },
    { href: "/about#team", label: "Team" },
    { href: "/careers", label: "Careers" },
  ];

  const legalCol = [
    ...(lending
      ? [
          { href: "/loan-terms", label: "Loan Terms & Conditions" },
          { href: "/responsible-lending", label: "Responsible Lending & Collections" },
        ]
      : []),
    { href: "/terms", label: "Terms of Service" },
    { href: "/privacy", label: "Privacy Policy" },
    { href: "/cookies", label: "Cookie Policy" },
  ];

  const helpCol = [
    { href: "/faq", label: "Help centre & FAQ" },
    ...(lending ? [{ href: "/struggling-to-repay", label: "Struggling to repay?" }] : []),
    { href: "/complaints", label: "Make a complaint" },
  ];

  return (
    <footer className="bg-[#0A3622] text-white/80 mt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
        {/* Brand block */}
        <div className="lg:col-span-1">
          <Logo variant="dark" testid="footer-logo" />
          <p className="mt-4 text-sm leading-relaxed text-white/60">
            Pay school fees monthly or in full. Ascentremit pays the school upfront, so your child
            starts term on time.
          </p>
        </div>

        {instalmentsCol && (
          <FooterCol title="Instalments" testid="footer-col-instalments">
            {instalmentsCol.map((l) => (
              <FooterLink key={l.label} href={l.href} label={l.label} />
            ))}
          </FooterCol>
        )}

        <FooterCol title="Company" testid="footer-col-company">
          {companyCol.map((l) => (
            <FooterLink key={l.label} href={l.href} label={l.label} />
          ))}
        </FooterCol>

        <FooterCol title="Legal" testid="footer-col-legal">
          {legalCol.map((l) => (
            <FooterLink key={l.label} href={l.href} label={l.label} />
          ))}
        </FooterCol>

        <FooterCol title="Help" testid="footer-col-help">
          {helpCol.map((l) => (
            <FooterLink key={l.label} href={l.href} label={l.label} />
          ))}
          {has(SITE.SUPPORT_EMAIL) && (
            <li>
              <a href={`mailto:${SITE.SUPPORT_EMAIL}`} className="hover:text-white" data-testid="footer-email">
                {SITE.SUPPORT_EMAIL}
              </a>
            </li>
          )}
          {has(SITE.PHONE_1) && (
            <li>
              <a href={`tel:${SITE.PHONE_1.replace(/\s/g, "")}`} className="hover:text-white" data-testid="footer-phone-1">
                {SITE.PHONE_1}
              </a>
            </li>
          )}
          {has(SITE.PHONE_2) && (
            <li>
              <a href={`tel:${SITE.PHONE_2.replace(/\s/g, "")}`} className="hover:text-white" data-testid="footer-phone-2">
                {SITE.PHONE_2}
              </a>
            </li>
          )}
          {has(SITE.WHATSAPP) && (
            <li>
              <a
                href={`https://wa.me/${SITE.WHATSAPP.replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                className="hover:text-white"
                data-testid="footer-whatsapp"
              >
                WhatsApp
              </a>
            </li>
          )}
        </FooterCol>
      </div>

      {/* Small-print block */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-16 py-8 space-y-2 text-xs text-white/55 leading-relaxed">
          {has(SITE.COMPANY_LEGAL_NAME) && has(SITE.CAC_RC_NUMBER) && has(SITE.REGISTERED_ADDRESS) && (
            <p data-testid="footer-smallprint-company">
              Ascentremit is a trading name of {SITE.COMPANY_LEGAL_NAME}, registered in Nigeria with
              the Corporate Affairs Commission, {SITE.CAC_RC_NUMBER}. Registered office:{" "}
              {SITE.REGISTERED_ADDRESS}.
            </p>
          )}
          {lending && has(SITE.LENDING_ENTITY) && (
            <p data-testid="footer-smallprint-lending">
              School-fee instalment plans are loans provided by {SITE.LENDING_ENTITY}. Loans are
              subject to an affordability assessment and approval.
            </p>
          )}
          {lending && has(SITE.CREDIT_BUREAU) && (
            <p data-testid="footer-smallprint-bureau">
              Repayment information is shared with {SITE.CREDIT_BUREAU}. Missed payments may affect
              your ability to borrow in future.
            </p>
          )}
          <p>
            Card and bank payments are processed by Paystack Payments Ltd (Nigeria) and Stripe
            (international).
          </p>
          {has(SITE.PRIVACY_EMAIL) && (
            <p>
              We process personal data under the Nigeria Data Protection Act 2023. Data protection
              contact: {SITE.PRIVACY_EMAIL}.
            </p>
          )}
          <p className="pt-2 text-white/45">
            © {new Date().getFullYear()} {SITE.COMPANY_LEGAL_NAME}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="overline mb-4 !text-[#F2B632]">{title}</p>
      <ul className="space-y-2 text-sm">{children}</ul>
    </div>
  );
}

function FooterLink({ href, label }) {
  const isHash = href.startsWith("/#");
  const external = href.startsWith("http");
  if (external) {
    return (
      <li>
        <a href={href} className="hover:text-white" target="_blank" rel="noreferrer">
          {label}
        </a>
      </li>
    );
  }
  if (isHash) {
    return (
      <li>
        <a href={href} className="hover:text-white">
          {label}
        </a>
      </li>
    );
  }
  return (
    <li>
      <Link to={href} className="hover:text-white">
        {label}
      </Link>
    </li>
  );
}
